// TODO: Use this csv utility module for visit-log as well if we want to customize its csv export (currently uses Ag-Grid default.)
// ^- lot of overlap, since the detailed view and visit log use the same dto (mumms.pimreaderVisit, getvisits res).

import * as XLSX from 'xlsx';
import {
  curry,
  flowRight as compose,
  filter,
  map,
  property,
  reduce,
  cond,
  curryRight,
} from 'lodash-es';
import moment from 'moment';
import { formatMMtoHHMM } from '@mummssoftware/utils/formatters';

const nullPimreaderVal = '[null]';

const isDefined = (x: any) => x === 0 || (!!x && x !== nullPimreaderVal);

const otherwise = () => true;

const outputBlank = () => '';

const definedAnd = (applyFunc: (val: any) => any) =>
  cond([
    [isDefined, applyFunc],
    [otherwise, outputBlank],
  ]);

const curriedMap = curryRight(map);

const downloadUrl = (url: string, fileName?: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'test.csv';
  document.body.appendChild(a);
  a.click();
};

const downloadCSV = (csvText: string) => {
  const blob = new Blob([csvText], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  downloadUrl(url, `Visit Details`);
};

const formatDateTimeMMDDYYhhmm = (dateTime: any) =>
  moment(dateTime).format('MM-DD-YY hh:mm');

const hasEndDateTime = (visit: mumms.pimreaderVisit | any) =>
  visit.starteventdatetime_shift && visit.durationmins;

const calculateEndDateTime = (visit: mumms.pimreaderVisit | any) =>
  moment(visit.starteventdatetime_shift).add(visit.durationmins, 'minute');

const calcEndDateTimeIfValid = cond([
  [hasEndDateTime, calculateEndDateTime as any],
  [otherwise, outputBlank],
]);

const formatValueUnit = curry((unit: string, value: any) => `${value} ${unit}`);

const formatDistance = definedAnd(formatValueUnit('miles'));

const formatDollars = definedAnd(formatValueUnit('$'));

const formatHours = definedAnd(formatValueUnit('h'));

const formatDateIfDefined = definedAnd(formatDateTimeMMDDYYhhmm);

const attributesToString = (visitAttributes: mumms.VisitAttribute[] | any) =>
  map(
    filter(visitAttributes, (curr) => curr.isselected && isDefined(curr.name)),
    'name',
  ).join(', ');

const columnGetter = curry((visit: mumms.pimreaderVisit, getValue: any) =>
  getValue(visit),
);

const parseAttributes = compose(attributesToString, JSON.parse);

const getPersonFirstName = property('personfirstname');

const getPersonLastName = property('personlastname');

const getCredentials = property('personcredentials');

const getRoleName = property('rolename');

const getPersonId = property('personid');

const getPatientFirstName = property('patientfirstname');

const getPatientLastName = property('patientlastname');

// labelled 'Patient ID' in the UI
// todo travis typo
const getPatientNumber = property('paientnumber');

const getId = property('id');

const getStatus = property('status');

const getFaceToFace = property('facetofacevisit');

const getStartDateTime = compose(
  formatDateIfDefined,
  property('effectivedatetime_shifted'),
);

const getEndDateTime = compose(formatDateIfDefined, calcEndDateTimeIfValid);

const getDuration = compose(
  formatHours,
  formatMMtoHHMM,
  property('durationmins'),
);

const getRoleShortName = property('cvdrole');

const getType = property('cvdtype');

const getLOC = property('cvdlevelofcare');

const getLocation = property('cvdlocation');

const cvdFields = [getRoleShortName, getType, getLOC, getLocation];

// @note verify this is correct field; used as this looks the match the value of 'siteoffice' field used elsewhere as 'Office'
const getOffice = property('sitenickname');

// @note verify units for next four fields

const getMileage = compose(formatDistance, property('mileage'));

const getTolls = compose(formatDollars, property('tolls'));

const getTravelTime = compose(formatHours, property('traveltime'));

const getParkingFees = compose(formatDollars, property('parkingfees'));

// @note better represented as a composition, but cannot do so without using lodash/fp functions (accurate curreid join)
const getCompiledVisitType = (visit: mumms.pimreaderVisit) => {
  const applyGetter = columnGetter(visit);
  return map(cvdFields, applyGetter).join('-');
};

const getArriveDistance = compose(
  formatDistance,
  property('arrivedistancemiles'),
);

const getDepartDistance = compose(
  formatDistance,
  property('departdistancemiles'),
);

const parseAttsIfDefined = definedAnd(parseAttributes);

const getVisitAttributes = compose(
  parseAttsIfDefined,
  property('visitattrsumdto'),
);

const detailColumns = [
  getPersonLastName,
  getPersonFirstName,
  getCredentials,
  getRoleName,
  getPersonId,
  getPatientLastName,
  getPatientFirstName,
  getPatientNumber,
  getId,
  getStatus,
  getFaceToFace,
  getStartDateTime,
  getEndDateTime,
  getDuration,
  getCompiledVisitType,
  getRoleShortName,
  getType,
  getLOC,
  getLocation,
  getOffice,
  getMileage,
  getTolls,
  getTravelTime,
  getParkingFees,
  getArriveDistance,
  getDepartDistance,
  getVisitAttributes,
];

const makeRowDataArray = (visit: mumms.pimreaderVisit): string[] => {
  const applyColumnDef = columnGetter(visit);
  return map(detailColumns, (getter) => String(applyColumnDef(getter) ?? ''));
};

const csvQuote = (val: string) =>
  /[",\r\n]/.test(val) ? `"${val.replace(/"/g, '""')}"` : val;

const makeRowDataString = (visit: mumms.pimreaderVisit) =>
  makeRowDataArray(visit).map(csvQuote).join(',');

const buildCSVRows = (seed: any, curr: any) =>
  // eslint-disable-next-line prefer-template
  seed + '\r\n' + makeRowDataString(curr);

const headerCells = [
  'Provider First Name',
  'Provider Last Name',
  'Credentials',
  'Role Name',
  'Person ID',
  'Patient First Name',
  'Patient Last Name',
  'Patient ID',
  'Visit ID',
  'Visit Status',
  'Face to Face',
  'Start',
  'End',
  'Duration',
  'Compiled Type',
  'Role Code',
  'Type Code',
  'LOC',
  'Location',
  'Office',
  'Mileage',
  'Tolls',
  'Travel Time',
  'Parking Fees',
  'Arrive Distance',
  'Depart Distance',
  'Attributes',
];

const headerRow = headerCells.join(',');

const formatDetailedData = (visits: mumms.pimreaderVisit[]) =>
  reduce(visits, buildCSVRows, headerRow);

export const downloadDetailsCSV = (visits: mumms.pimreaderVisit[]) => {
  // todo new api call
  const csvText = formatDetailedData(visits);
  downloadCSV(csvText);
};

export const downloadDetailsExcel = (visits: mumms.pimreaderVisit[]) => {
  const rows = [headerCells, ...map(visits, makeRowDataArray)];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Visit details');
  XLSX.writeFile(wb, 'Visit Details.xlsx');
};
